import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, ClipboardCheck, Loader2, RefreshCw, Search, X } from "lucide-react";
import ReviewDrawer from "./ReviewDrawer";
import { FILTERS, metaFor, timeAgo, formatWhen } from "./meta";

const PAGE_SIZE = 25;

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function Toast({ toast, onClose }) {
  if (!toast) return null;
  const ok = toast.type === "success";
  return (
    <div role="status" className={`fixed right-4 top-20 z-[60] flex max-w-sm items-start gap-3 rounded-xl border px-4 py-3 text-[13px] shadow-lg ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-rose-200 bg-rose-50 text-rose-900"}`}>
      {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}
      <span className="whitespace-pre-wrap">{toast.msg}</span>
      <button type="button" onClick={onClose} aria-label="Dismiss" className="ml-1 shrink-0 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
    </div>
  );
}

function Kpi({ label, value, tone, active, onClick }) {
  return (
    <button type="button" onClick={onClick} className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition hover:shadow ${active ? "border-[#1565c0] ring-2 ring-[#1565c0]/15" : "border-[#e6edf8]"}`}>
      <div className="text-[11px] font-medium text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${tone}`}>{value}</div>
    </button>
  );
}

function BulkConfirm({ config, rows, busy, onCancel, onConfirm }) {
  const noun = config.entityLabel;
  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Confirm bulk approval">
      <button type="button" className="absolute inset-0 bg-slate-900/40" aria-label="Cancel" onClick={() => !busy && onCancel()} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <h3 className="m-0 text-[16px] font-bold text-slate-900">Approve {rows.length} {noun}{rows.length === 1 ? "" : "s"}?</h3>
        <p className="mt-1 text-[13px] text-slate-600">Each one is checked again as it is approved. Anything edited after you loaded this list is skipped and reported. Approving does not publish.</p>
        <ul className="mt-3 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-[#e6edf8] bg-[#f9fbff] p-2 text-[12px]">
          {rows.map((r) => (
            <li key={r.id} className="flex justify-between gap-3 rounded-lg bg-white px-2.5 py-1.5">
              <span className="font-medium text-slate-800">{config.titleOf(r)}</span>
              <span className="truncate text-slate-500">{r.teacher_name}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" disabled={busy} onClick={onCancel} className="rounded-xl border border-slate-300 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="button" disabled={busy} onClick={onConfirm} className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Approve {rows.length}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Generic reviewer dashboard used by every approval flow. `config`:
 *  title, subtitle, entityLabel, entityKey, icon?, api{queue,detail,review,bulk,unpublish},
 *  columns[{key,label,render(row)}], titleOf(row), subtitleOf(row), renderContent(detail).
 */
export default function ApprovalsDashboard({ config, embedded = false }) {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [teacher, setTeacher] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], counts: {}, total: 0, filters: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [openId, setOpenId] = useState(params.get("id") || "");
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [toast, setToast] = useState(null);
  const requestId = useRef(0);
  const debouncedSearch = useDebounced(search);
  const noun = config.entityLabel;
  const Icon = config.icon || ClipboardCheck;

  const toastTimer = useRef(null);
  const notify = useCallback((type, msg) => {
    setToast({ type, msg });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }, []);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const load = useCallback(async () => {
    const rid = ++requestId.current;
    setLoading(true); setError("");
    try {
      const res = await config.api.queue({ stage: filter, search: debouncedSearch || undefined, grade: grade || undefined, subject: subject || undefined, teacher_id: teacher || undefined, page, page_size: PAGE_SIZE });
      if (rid === requestId.current) setData(res);
    } catch (e) {
      if (rid === requestId.current) setError(e.message || `Could not load ${noun}s.`);
    } finally { if (rid === requestId.current) setLoading(false); }
  }, [config, filter, debouncedSearch, grade, subject, teacher, page, noun]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter, debouncedSearch, grade, subject, teacher]);
  useEffect(() => { setSelected(new Set()); }, [filter, debouncedSearch, grade, subject, teacher, page]);

  const items = useMemo(() => data.items || [], [data.items]);
  const approvable = useMemo(() => items.filter((r) => r.can_approve), [items]);
  const allSelected = approvable.length > 0 && approvable.every((r) => selected.has(r.id));
  const selectedRows = useMemo(() => items.filter((r) => selected.has(r.id)), [items, selected]);
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const openIndex = items.findIndex((r) => r.id === openId);
  const counts = data.counts || {};
  const byId = useMemo(() => Object.fromEntries(items.map((r) => [r.id, r])), [items]);

  const open = (id) => { setOpenId(id); const n = new URLSearchParams(params); n.set("id", id); setParams(n, { replace: true }); };
  const closeDrawer = useCallback(() => { setOpenId(""); const n = new URLSearchParams(params); n.delete("id"); setParams(n, { replace: true }); }, [params, setParams]);
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const onDecided = async (row, mode) => {
    const nextId = openIndex >= 0 ? items[openIndex + 1]?.id : "";
    const name = config.titleOf(row);
    notify("success", mode === "override" ? `${name}: HOD step skipped. It now waits for final approval.`
      : mode === "approve" ? (row.stage === "admin_review" ? `${name} approved. It stays private until the teacher publishes it.` : `${name} approved and sent to the admin.`)
      : mode === "unpublish" ? `${name} unpublished.` : `${name} sent back with your comments.`);
    await load();
    if (nextId) open(nextId); else closeDrawer();
  };

  const runBulk = async () => {
    setBulkBusy(true);
    try {
      const res = await config.api.bulk({ items: selectedRows.map((r) => ({ id: r.id, expected_revision: r.revision })), action: "approve" });
      const failed = (res.results || []).filter((r) => !r.ok);
      setBulkResult({ ...res, failed });
      notify(failed.length ? "error" : "success", `${res.succeeded} approved${failed.length ? `, ${failed.length} skipped` : ""}.`);
      setSelected(new Set()); setConfirmBulk(false);
      await load();
    } catch (e) { notify("error", e.message || "Bulk approval failed."); } finally { setBulkBusy(false); }
  };

  const f = data.filters || {};
  const selectCls = "rounded-xl border border-slate-300 bg-white px-2.5 py-2 text-[13px] text-slate-700";

  return (
    <div className={embedded ? "bg-white font-[poppins]" : "min-h-screen bg-white pt-16 font-[poppins]"}>
      <div className={embedded ? "w-full p-4" : "mx-auto w-full max-w-[1400px] px-4 pb-12 pt-4"}>
        <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
          {embedded ? (
            <div>
              <h2 className="m-0 text-lg font-semibold text-gray-900">{config.entityTitle || config.title}</h2>
              <p className="m-0 text-[13px] text-gray-500">{config.subtitle}</p>
            </div>
          ) : (
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#D67C2C]"><Icon className="h-6 w-6 text-white" /></div>
            <div>
              <h1 className="m-0 text-2xl font-bold text-[#D67C2C] sm:text-4xl">{config.title}</h1>
              <p className="m-0 text-[13px] text-[#1565c0]">{config.subtitle}</p>
            </div>
          </div>
          )}
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-xl border border-[#d9e7f7] bg-white px-3 py-2 text-[13px] font-medium text-[#1565c0] hover:bg-[#f4f8fe]">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </header>

        <section className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5" aria-label="Summary">
          <Kpi label="Needs my review" value={counts.pending ?? 0} tone="text-[#1565c0]" active={filter === "pending"} onClick={() => setFilter("pending")} />
          <Kpi label="Awaiting HOD" value={counts.hod_review ?? 0} tone="text-amber-600" active={filter === "hod_review"} onClick={() => setFilter("hod_review")} />
          <Kpi label="Awaiting Admin" value={counts.admin_review ?? 0} tone="text-blue-600" active={filter === "admin_review"} onClick={() => setFilter("admin_review")} />
          <Kpi label="Changes requested" value={counts.changes_requested ?? 0} tone="text-rose-600" active={filter === "changes_requested"} onClick={() => setFilter("changes_requested")} />
          <Kpi label="Published" value={counts.published ?? 0} tone="text-teal-600" active={filter === "published"} onClick={() => setFilter("published")} />
        </section>

        <section className="rounded-2xl border border-[#e6edf8] bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-3 border-b border-[#e6edf8] p-3">
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Status filter">
              {FILTERS.map((x) => (
                <button key={x.key} type="button" role="tab" aria-selected={filter === x.key} onClick={() => setFilter(x.key)}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition ${filter === x.key ? "border-[#1565c0] bg-[#1565c0] text-white" : "border-[#d9e7f7] bg-white text-slate-600 hover:bg-[#f4f8fe]"}`}>
                  {x.label}{x.key !== "all" && <span className={`ml-1.5 ${filter === x.key ? "text-white/80" : "text-slate-400"}`}>{counts[x.key] ?? 0}</span>}
                </button>
              ))}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <select value={grade} onChange={(e) => setGrade(e.target.value)} aria-label="Filter by grade" className={selectCls}>
                <option value="">All grades</option>{(f.grades || []).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Filter by subject" className={selectCls}>
                <option value="">All subjects</option>{(f.subjects || []).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={teacher} onChange={(e) => setTeacher(e.target.value)} aria-label="Filter by teacher" className={selectCls}>
                <option value="">All teachers</option>{(f.teachers || []).map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
              </select>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${noun}s…`} aria-label={`Search ${noun}s`} className="w-52 rounded-xl border border-slate-300 py-2 pl-8 pr-3 text-[13px] focus:border-[#1565c0] focus:outline-none focus:ring-2 focus:ring-[#1565c0]/20" />
              </div>
            </div>
          </div>

          {selected.size > 0 && (
            <div className={`${embedded ? "" : "sticky top-16 "}z-10 flex flex-wrap items-center justify-between gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-2.5`}>
              <span className="text-[13px] font-medium text-emerald-900">{selected.size} {noun}{selected.size === 1 ? "" : "s"} selected</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSelected(new Set())} className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-emerald-900 hover:bg-emerald-100">Clear</button>
                <button type="button" onClick={() => setConfirmBulk(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700"><CheckCircle2 className="h-4 w-4" /> Approve selected</button>
              </div>
            </div>
          )}

          {bulkResult?.failed?.length > 0 && (
            <div className="border-b border-rose-200 bg-rose-50 px-4 py-3 text-[12px] text-rose-900">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-semibold">{bulkResult.succeeded} approved, {bulkResult.failed.length} skipped</span>
                <button type="button" onClick={() => setBulkResult(null)} aria-label="Dismiss" className="opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
              </div>
              <ul className="m-0 list-disc pl-5">{bulkResult.failed.map((x) => <li key={x.id}>{byId[x.id] ? config.titleOf(byId[x.id]) : x.id}: {x.error}</li>)}</ul>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-left">
              <thead>
                <tr className="bg-[#f9fbff] text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="w-10 px-4 py-3"><input type="checkbox" aria-label={`Select all approvable ${noun}s on this page`} checked={allSelected} disabled={approvable.length === 0} onChange={() => setSelected(allSelected ? new Set() : new Set(approvable.map((r) => r.id)))} /></th>
                  {config.columns.map((c) => <th key={c.key} className="px-3 py-3 font-semibold">{c.label}</th>)}
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-3 py-3 font-semibold">Submitted</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const meta = metaFor(r.display_status, r.stage);
                  return (
                    <tr key={r.id} className="cursor-pointer border-t border-[#eef3fa] text-[13px] hover:bg-[#f4f8fe]" onClick={() => open(r.id)}>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Select ${config.titleOf(r)}`} checked={selected.has(r.id)} disabled={!r.can_approve} title={r.can_approve ? "" : "Not awaiting your approval"} onChange={() => toggle(r.id)} />
                      </td>
                      {config.columns.map((c) => <td key={c.key} className="px-3 py-3 text-slate-700">{c.render(r)}</td>)}
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${meta.pill}`}><span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />{meta.label}</span>
                        {r.hod_skipped_by && <span className="ml-1.5 text-[11px] font-medium text-amber-700" title={`HOD approval skipped by ${r.hod_skipped_by}`}>HOD skipped</span>}
                        {r.unassigned && <span className="ml-1.5 text-[11px] font-medium text-slate-500" title="This teacher is not in any department">no department</span>}
                      </td>
                      <td className="px-3 py-3 text-[12px] text-slate-500" title={formatWhen(r.submitted_at)}>{r.submitted_at ? timeAgo(r.submitted_at) : "—"}</td>
                      <td className="px-3 py-3 text-right"><span className="text-[12px] font-semibold text-[#1565c0]">{r.can_approve || r.can_override_hod ? "Review" : "View"}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {loading && items.length === 0 && <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
            {!loading && error && <div className="m-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-[13px] text-rose-800">{error}</div>}
            {!loading && !error && data.no_department && <div className="py-16 text-center text-[13px] text-slate-500">{data.message}<br />Ask an admin to add you as the head of a department.</div>}
            {!loading && !error && !data.no_department && items.length === 0 && <div className="py-16 text-center text-[13px] text-slate-500">{filter === "pending" ? "Nothing is waiting for your review. 🎉" : `No ${noun}s match these filters.`}</div>}
          </div>

          <div className="flex items-center justify-between border-t border-[#e6edf8] px-4 py-3 text-[12px] text-slate-500">
            <span>{data.total || 0} {noun}{data.total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 font-medium text-slate-700 disabled:opacity-40">Previous</button>
              <span>Page {page} of {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 font-medium text-slate-700 disabled:opacity-40">Next</button>
            </div>
          </div>
        </section>
      </div>

      {openId && (
        <ReviewDrawer key={openId} config={config} id={openId} onClose={closeDrawer} onDecided={onDecided}
          position={openIndex >= 0 ? `${openIndex + 1} of ${items.length}` : ""}
          onPrev={openIndex > 0 ? () => open(items[openIndex - 1].id) : null}
          onNext={openIndex >= 0 && openIndex < items.length - 1 ? () => open(items[openIndex + 1].id) : null} />
      )}
      {confirmBulk && <BulkConfirm config={config} rows={selectedRows} busy={bulkBusy} onCancel={() => setConfirmBulk(false)} onConfirm={runBulk} />}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
